export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;
  data?: unknown;
  /** Whether `message` came from the server's own envelope, vs. a caller-supplied fallback
   *  (e.g. axios's "Request failed with status code 404"). Lets a consumer prefer a code-keyed
   *  copy table over a fallback without discarding a message the server actually sent. */
  hasServerMessage: boolean;
  constructor(
    message: string,
    status: number,
    opts: {
      code?: string;
      details?: Record<string, unknown>;
      data?: unknown;
      hasServerMessage?: boolean;
    } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = opts.code;
    this.details = opts.details;
    this.data = opts.data;
    this.hasServerMessage = opts.hasServerMessage ?? false;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'Unauthorized', data?: unknown) {
    super(message, 401, { data });
    this.name = 'UnauthorizedError';
  }
}

/** ADR-0008's error envelope shape. Exported so a caller holding a bare `ApiError.data` — e.g. to
 *  prefer the server's own message over the axios fallback baked into `.message` — can read it
 *  without re-declaring the shape. */
export type Envelope = {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
};

export function parseApiError(
  status: number,
  data: unknown,
  fallbackMessage = 'Request failed',
): ApiError {
  const envelope = (data ?? undefined) as Envelope | undefined;
  const error = envelope?.error;
  return new ApiError(error?.message ?? fallbackMessage, status, {
    code: error?.code,
    details: error?.details,
    data,
    hasServerMessage: Boolean(error?.message),
  });
}
